import { useMemo, useSyncExternalStore } from "react";
import type { NikolaActivity } from "@chessla/shared";
import type { NikolaMessageInput, SendMessageResult } from "./chat-contracts";
import { useChatStore } from "./chat.store";
import { chatDraftKey } from "./chat-view-state";
import {
  cancelNikolaRun,
  createNikolaRun,
  recoverNikolaRun,
  submitNikolaBrowserResult,
  type NikolaBrowserResultInput,
  type NikolaRunInput,
} from "./nikola-run.api";

export const toNikolaRunInput = (input: NikolaMessageInput): NikolaRunInput => {
  const gameId = input.reviewMoment?.gameId ?? input.context?.gameId;
  return {
    sessionId: input.sessionId,
    message: input.message,
    requestId: input.requestId,
    context: input.study
      ? {
          surface: "board_study",
          study: input.study,
          ...(input.includeFullGameAnalysis || input.studyAnalysis
            ? {
                includeFullGameAnalysis: true,
                ...(input.studyAnalysis ? { studyAnalysis: input.studyAnalysis } : {}),
              }
            : {}),
        }
      : {
          surface: gameId ? "game_review" : "chat",
          ...(gameId ? { gameId } : {}),
          ...(input.context?.ply ? { ply: input.context.ply } : {}),
          ...(input.context?.moveNumber ? { moveNumber: input.context.moveNumber } : {}),
        },
  };
};

export interface NikolaRunClientState {
  phase: "idle" | "ready" | "running" | "waiting" | "completed" | "failed" | "cancelled";
  runId: string | null;
}

export type NikolaRunClientAction =
  | { type: "start" }
  | { type: "empty" }
  | { type: "created"; runId: string }
  | { type: "result"; waiting: boolean; failed?: boolean }
  | { type: "failed" }
  | { type: "cancelled" };

export const initialNikolaRunClientState: NikolaRunClientState = { phase: "idle", runId: null };

export const nikolaRunClientReducer = (
  state: NikolaRunClientState,
  action: NikolaRunClientAction,
): NikolaRunClientState => {
  switch (action.type) {
    case "start":
      return { phase: "running", runId: null };
    case "empty":
      return { phase: "ready", runId: null };
    case "created":
      return { phase: "running", runId: action.runId };
    case "result":
      return { phase: action.failed ? "failed" : action.waiting ? "waiting" : "completed", runId: state.runId };
    case "failed":
      return { phase: "failed", runId: state.runId };
    case "cancelled":
      return { phase: "cancelled", runId: null };
  }
};

export interface NikolaRunPorts {
  create: typeof createNikolaRun;
  submit: typeof submitNikolaBrowserResult;
  cancel: typeof cancelNikolaRun;
  recover: typeof recoverNikolaRun;
}

const defaultPorts: NikolaRunPorts = {
  create: createNikolaRun,
  submit: submitNikolaBrowserResult,
  cancel: cancelNikolaRun,
  recover: recoverNikolaRun,
};

export class NikolaRunExecutor {
  private state: NikolaRunClientState = initialNikolaRunClientState;
  private readonly listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private operation = 0;
  private result: SendMessageResult | null = null;
  private error: unknown = null;
  private disposed = false;

  constructor(
    private readonly ports: NikolaRunPorts = defaultPorts,
    private readonly sessionId?: string | null,
  ) {}

  readonly getSnapshot = (): NikolaRunClientState => this.state;
  readonly getResult = (): SendMessageResult | null => this.result;
  readonly getError = (): unknown => this.error;
  readonly hasSubscribers = (): boolean => this.listeners.size > 0;
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private dispatch(action: NikolaRunClientAction): void {
    this.state = nikolaRunClientReducer(this.state, action);
    this.listeners.forEach((listener) => listener());
  }

  private begin(): { signal: AbortSignal; operation: number } {
    if (this.disposed || this.sessionId === null)
      throw new DOMException("Nikola session is unavailable.", "AbortError");
    this.controller?.abort();
    this.controller = new AbortController();
    this.operation += 1;
    this.result = null;
    this.error = null;
    this.dispatch({ type: "start" });
    return { signal: this.controller.signal, operation: this.operation };
  }

  readonly signal = (): AbortSignal => {
    if (this.disposed) throw new DOMException("Nikola session is unavailable.", "AbortError");
    if (!this.controller || this.controller.signal.aborted) this.controller = new AbortController();
    return this.controller.signal;
  };

  readonly execute = async (
    input: NikolaMessageInput,
    onActivity: (activity: NikolaActivity) => void,
  ): Promise<SendMessageResult> => {
    if (this.sessionId !== undefined && input.sessionId !== this.sessionId) throw new Error("Nikola session mismatch.");
    const active = this.begin();
    try {
      const result = await this.ports.create(
        toNikolaRunInput(input),
        (activity) => {
          if (!active.signal.aborted && active.operation === this.operation) onActivity(activity);
        },
        active.signal,
        (runId) => {
          if (active.signal.aborted || active.operation !== this.operation) return;
          this.dispatch({ type: "created", runId });
        },
      );
      this.assertCurrent(active);
      if (!active.signal.aborted && active.operation === this.operation) {
        this.result = result;
        this.dispatch({ type: "result", waiting: Boolean(result.pendingAction), failed: result.status === "failed" });
      }
      return result;
    } catch (error) {
      if (active.operation === this.operation) {
        this.error = error;
        this.dispatch({ type: active.signal.aborted ? "cancelled" : "failed" });
      }
      throw error;
    }
  };

  readonly submit = async (
    input: NikolaBrowserResultInput,
    onActivity: (activity: NikolaActivity) => void,
  ): Promise<SendMessageResult> => {
    if (this.sessionId !== undefined && input.runId !== this.state.runId) throw new Error("Nikola run mismatch.");
    const active = this.begin();
    this.dispatch({ type: "created", runId: input.runId });
    try {
      const result = await this.ports.submit(
        input,
        (activity) => {
          if (!active.signal.aborted && active.operation === this.operation) onActivity(activity);
        },
        active.signal,
      );
      this.assertCurrent(active);
      if (!active.signal.aborted && active.operation === this.operation) {
        this.result = result;
        this.dispatch({ type: "result", waiting: Boolean(result.pendingAction), failed: result.status === "failed" });
      }
      return result;
    } catch (error) {
      if (active.operation === this.operation) {
        this.error = error;
        this.dispatch({ type: active.signal.aborted ? "cancelled" : "failed" });
      }
      throw error;
    }
  };

  readonly recover = async (
    sessionId: string,
    onActivity: (activity: NikolaActivity) => void,
  ): Promise<SendMessageResult | null> => {
    if (this.sessionId !== undefined && sessionId !== this.sessionId) throw new Error("Nikola session mismatch.");
    const active = this.begin();
    try {
      const result = await this.ports.recover(
        sessionId,
        (activity) => {
          if (!active.signal.aborted && active.operation === this.operation) onActivity(activity);
        },
        active.signal,
        (runId) => {
          if (active.signal.aborted || active.operation !== this.operation) return;
          this.dispatch({ type: "created", runId });
        },
      );
      this.assertCurrent(active);
      if (!result) this.dispatch({ type: "empty" });
      if (result && !active.signal.aborted && active.operation === this.operation) {
        this.result = result;
        this.dispatch({ type: "result", waiting: Boolean(result.pendingAction), failed: result.status === "failed" });
      }
      return result;
    } catch (error) {
      if (active.operation === this.operation) {
        this.error = error;
        this.dispatch({ type: active.signal.aborted ? "cancelled" : "failed" });
      }
      throw error;
    }
  };

  readonly cancel = (): void => {
    this.controller?.abort();
    this.controller = null;
    this.operation += 1;
    this.result = null;
    const runId = this.state.runId;
    this.dispatch({ type: "cancelled" });
    if (runId) void this.ports.cancel(runId).catch(() => undefined);
  };

  readonly fail = (error: unknown): void => {
    this.controller?.abort();
    this.operation += 1;
    this.result = null;
    this.error = error;
    this.dispatch({ type: "failed" });
  };

  private assertCurrent(active: { signal: AbortSignal; operation: number }): void {
    if (active.signal.aborted || active.operation !== this.operation)
      throw new DOMException("Nikola request was detached.", "AbortError");
  }

  readonly dispose = (): void => {
    this.disposed = true;
    this.controller?.abort();
    this.operation += 1;
    this.result = null;
    this.error = null;
    this.dispatch({ type: "cancelled" });
    this.listeners.clear();
  };
}

export class NikolaRunSessions {
  private owner: string | null = null;
  private readonly sessions = new Map<string, NikolaRunExecutor>();
  private readonly unavailable = new NikolaRunExecutor(defaultPorts, null);

  constructor(private readonly ports: NikolaRunPorts = defaultPorts) {}

  setOwner(owner: string | null): void {
    try {
      const ownerKey = "chessla.nikola-chat-owner";
      if (!owner || localStorage.getItem(ownerKey) !== owner) {
        for (let index = localStorage.length - 1; index >= 0; index -= 1) {
          const key = localStorage.key(index);
          if (key?.startsWith(chatDraftKey(""))) localStorage.removeItem(key);
        }
      }
      if (owner) localStorage.setItem(ownerKey, owner);
      else localStorage.removeItem(ownerKey);
    } catch {
      // Storage may be unavailable; private in-memory state must still be cleared.
    }
    if (owner === this.owner) return;
    this.owner = owner;
    useChatStore.setState({ playerId: null, sessionId: null, messages: [] });
    this.sessions.forEach((executor) => executor.dispose());
    this.sessions.clear();
  }

  get(owner: string | null, sessionId: string | null): NikolaRunExecutor {
    this.setOwner(owner);
    if (!owner || !sessionId) return this.unavailable;
    const existing = this.sessions.get(sessionId);
    if (existing) {
      if (existing.hasSubscribers() || ["running", "waiting", "idle"].includes(existing.getSnapshot().phase))
        return existing;
      existing.dispose();
      this.sessions.delete(sessionId);
    }
    if (this.sessions.size >= 16) {
      const detached = [...this.sessions].find(([, executor]) => !executor.hasSubscribers());
      if (!detached) throw new Error("Too many active Nikola conversations.");
      detached[1].dispose();
      this.sessions.delete(detached[0]);
    }
    const executor = new NikolaRunExecutor(this.ports, sessionId);
    this.sessions.set(sessionId, executor);
    return executor;
  }
}

export const nikolaRunSessions = new NikolaRunSessions();

export const useNikolaRun = (owner: string | null, sessionId: string | null) => {
  const executor = useMemo(() => nikolaRunSessions.get(owner, sessionId), [owner, sessionId]);
  const state = useSyncExternalStore(executor.subscribe, executor.getSnapshot, executor.getSnapshot);
  return {
    state,
    result: executor.getResult(),
    error: executor.getError(),
    execute: executor.execute,
    submit: executor.submit,
    recover: executor.recover,
    cancel: executor.cancel,
    fail: executor.fail,
    signal: executor.signal,
  };
};
